import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import PageLoader from "./PageLoader";
import {
  fetchCurrentUserInfo, fetchProfile, updateProfile, updateUserEmail,
  fetchCountries, fetchStates, setReferralByCode, fetchMemberById, isAdminUser, fetchTransactions, createTransaction
} from "../script/rpc";
import { getCookie } from "../script/cookies";

const FATAL_AUTH_ERRORS = [
  "could not fetch user information", "could not load user partner account",
  "connection failed", "network error", "invalid username", "access denied",
];
function isFatalAuthError(msg = "") {
  const lower = msg.toLowerCase();
  return FATAL_AUTH_ERRORS.some(p => lower.includes(p));
}

const TABS = [
  { id: "personal", label: "Personal", },
  { id: "address", label: "Address", },
  { id: "payment", label: "Bank Details", },
];

const PAYMENT_TYPES = [
  { value: "bank", label: "Bank Transfer", icon: "🏦" },
  // { value: "upi",     label: "UPI",           icon: "📱" },
  // { value: "gateway", label: "Payment Gateway", icon: "🌐" },
  // { value: "cash",    label: "Cash",          icon: "💵" },
];



const ACCOUNT_TYPES = [
  { value: "saving", label: "Savings" },
  { value: "current", label: "Current" },
  { value: "salary", label: "Salary" },
];

export default function ProfilePage({ uid, password, onLogout }) {
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("personal");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [partnerId, setPartnerId] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [transactions, setTransactions] = useState([]);

  console.log("Transactions:", transactions);

  const [formData, setFormData] = useState({
    // Personal
    name: "", email: "", phone: "", ltx: "", sponsor_code: "", mlm_sequence: "", parent_id: "",
    // Address
    street: "", street2: "", city: "", zip: "", country_id: "", state_id: "",
    // Payment
    payment_type: "bank",
    bank_account_name: "", bank_account_number: "", bank_account_type: "",
    bank_ifsc_code: "", bank_branch: "",
    upi_reference_code: "",
    payment_gateway_reference: "",
    bank_name: "",
  });
  console.log(formData)


  // Dropdown state
  const [countrySearch, setCountrySearch] = useState("");
  const [stateSearch, setStateSearch] = useState("");
  const [showCountryDd, setShowCountryDd] = useState(false);
  const [showStateDd, setShowStateDd] = useState(false);
  const countryRef = useRef(null);
  const stateRef = useRef(null);
  const initialLtxRef = useRef("");

  const filteredCountries = countries.filter(c => c.name.toLowerCase().includes(countrySearch.toLowerCase()));
  const filteredStates = states.filter(s => s.name.toLowerCase().includes(stateSearch.toLowerCase()));
  const selectedCountry = countries.find(c => c.id === formData.country_id);
  const selectedState = states.find(s => s.id === formData.state_id);

  // Close dropdowns on outside click
  useEffect(() => {
    const handle = (e) => {
      if (countryRef.current && !countryRef.current.contains(e.target)) {
        setShowCountryDd(false); setCountrySearch("");
      }
      if (stateRef.current && !stateRef.current.contains(e.target)) {
        setShowStateDd(false); setStateSearch("");
      }
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, []);


  useEffect(() => {
    // transactions will be loaded after profile (partnerId) is known
  }, [uid, password]);





  // Load profile
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const userInfo = await fetchCurrentUserInfo(uid, password);
        if (!alive) return;
        if (!userInfo?.partner_id) throw new Error("Could not load user partner account.");
        const pid = userInfo.partner_id[0];
        setPartnerId(pid);
        const [profile, countryList, adminCheck] = await Promise.all([
          fetchProfile(uid, password, pid),
          fetchCountries(uid, password),
          isAdminUser(uid, password),
        ]);
        if (!alive) return;
        setCountries(countryList);
        setIsAdmin(adminCheck);

        // Set India as default country if none is set on the profile
        const indiaDefault = !profile?.country_id
          ? (countryList.find(c => c.name === "India")?.id || "")
          : "";

        let fetchedSponsorCode = "";
        const initialParentId = profile?.parent_id ? profile.parent_id[0] : "";
        if (initialParentId) {
          const sp = await fetchMemberById(uid, password, initialParentId);
          if (sp?.mlm_sequence) fetchedSponsorCode = sp.mlm_sequence;
        }

        if (profile) {
          setFormData({
            ltx: profile.ltx ? (profile.ltx.startsWith("LTX") ? profile.ltx : "LTX" + profile.ltx) : (profile.phone ? "LTX" + profile.phone : ""),
            name: profile.name || "",
            email: userInfo?.email || profile.email || "",
            phone: profile.phone || "",
            sponsor_code: fetchedSponsorCode,
            mlm_sequence: profile.mlm_sequence || "",
            parent_id: initialParentId,
            street: profile.street || "",
            street2: profile.street2 || "",
            city: profile.city || "",
            zip: profile.zip || "",
            country_id: profile.country_id ? profile.country_id[0] : indiaDefault,
            state_id: profile.state_id ? profile.state_id[0] : "",
            payment_type: profile.payment_type || "bank",
            bank_account_name: profile.bank_account_name || "",
            bank_account_number: profile.bank_account_number || "",
            bank_account_type: profile.bank_account_type || "",
            bank_ifsc_code: profile.bank_ifsc_code || "",
            bank_branch: profile.bank_branch || "",
            bank_name: profile.bank_name || "",
            upi_reference_code: profile.upi_reference_code || "",
            payment_gateway_reference: profile.payment_gateway_reference || "",
          });
          if (profile.country_id) {
            const stateList = await fetchStates(uid, password, profile.country_id[0]);
            if (alive) setStates(stateList);
          } else if (indiaDefault) {
            const stateList = await fetchStates(uid, password, indiaDefault);
            if (alive) setStates(stateList);
          }

          // Fetch recent transactions for this partner and populate latest LTX if available
          try {
            const txs = await fetchTransactions(uid, password, pid);
            if (alive && Array.isArray(txs) && txs.length > 0) {
              // pick the latest by date if trans_date exists, fallback to first
              const sorted = txs.slice().sort((a, b) => {
                const da = a.trans_date ? new Date(a.trans_date) : new Date(0);
                const db = b.trans_date ? new Date(b.trans_date) : new Date(0);
                return db - da;
              });
              const latest = sorted[0];
              if (latest?.ltx) {
                const val = latest.ltx.startsWith("LTX") ? latest.ltx : "LTX" + latest.ltx;
                setFormData(prev => ({ ...prev, ltx: val }));
                initialLtxRef.current = val;
              } else {
                // if no transaction ltx, use profile.ltx or phone as initial
                const val = profile.ltx ? (profile.ltx.startsWith("LTX") ? profile.ltx : "LTX" + profile.ltx) : (profile.phone ? "LTX" + profile.phone : "");
                initialLtxRef.current = val;
              }
              setTransactions(txs || []);
            } else {
              initialLtxRef.current = profile.ltx || profile.phone || "";
            }
          } catch (e) {
            console.error("Failed to load transactions:", e);
            initialLtxRef.current = profile.ltx || profile.phone || "";
          }
        }

        // Auto-fill referral from URL/Cookie if not already set by profile
        if (alive) {
          const cookieRef = getCookie("mlm_referral_code");
          if (cookieRef && !profile?.parent_id) {
            setFormData(prev => ({ ...prev, sponsor_code: cookieRef }));
          }
        }
      } catch (err) {
        if (alive) {
          setError(err.message);
          if (isFatalAuthError(err.message)) setTimeout(() => onLogout?.(), 2000);
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [uid, password]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: (name.endsWith("_id") && value) ? Number(value) : value }));
  };

  const handlePhoneChange = (e) => {
    const raw = e.target.value || "";
    const value = raw.replace(/\D/g, "").slice(0, 10);
    setFormData(prev => ({ ...prev, phone: value, ltx: value ? "LTX" + value : "" }));
  };

  const handleCountryChange = async (cId) => {
    setFormData(prev => ({ ...prev, country_id: cId, state_id: "" }));
    setShowCountryDd(false); setCountrySearch("");
    if (cId) {
      const stateList = await fetchStates(uid, password, cId);
      setStates(stateList);
    } else {
      setStates([]);
    }
  };

  const handleSubmit = async () => {
    if (!partnerId) return;

    if (
      formData.payment_type === "bank" &&
      (
        !formData.bank_account_name?.trim() ||
        !formData.bank_account_number?.trim() ||
        !formData.bank_account_type ||
        !formData.bank_ifsc_code?.trim() ||
        !formData.bank_branch?.trim() ||
        !formData.bank_name?.trim()
      )
    ) {
      setError("Please fill all bank details.");
      return;
    }

    if
      (!formData.street || !formData.street2 || !formData.city || !formData.zip || !formData.country_id || !formData.state_id) {
      setActiveTab('address')
      setError("All The Fields are Required..")
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    if (!formData.email || !formData.phone) {
      setActiveTab("personal");
      setError("Email and Phone are required fields");
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    const scode = formData.sponsor_code;
    setSaving(true); setError(""); setSuccess("");

    if (scode && !isAdmin) {
      try {
        const isValid = await setReferralByCode(uid, password, partnerId, scode);
        if (!isValid) {
          setError("Invalid Referral Code. Please double-check and try again.");
          setSaving(false); return;
        }
      } catch {
        setError("Failed to verify referral code.");
        setSaving(false); return;
      }
    }

    try {
      // Build payment payload — only include relevant fields based on payment_type
      const pt = formData.payment_type;
      const paymentPayload = {
        payment_type: pt || false,
        bank_account_name: pt === "bank" ? formData.bank_account_name : false,
        bank_account_number: pt === "bank" ? formData.bank_account_number : false,
        bank_account_type: pt === "bank" ? (formData.bank_account_type || false) : false,
        bank_ifsc_code: pt === "bank" ? formData.bank_ifsc_code : false,
        bank_branch: pt === "bank" ? formData.bank_branch : false,
        bank_name: pt === "bank" ? formData.bank_name : false,
        upi_reference_code: pt === "upi" ? formData.upi_reference_code : false,
        payment_gateway_reference: pt === "gateway" ? formData.payment_gateway_reference : false,
      };

      await updateProfile(uid, password, partnerId, {
        street: formData.street,
        street2: formData.street2,
        city: formData.city,
        zip: formData.zip,
        phone: formData.phone,
        ltx: formData.ltx ? formData.ltx.replace(/\s+/g, '') : "",
        country_id: formData.country_id || false,
        state_id: formData.state_id || false,
        ...paymentPayload,
      });

      if (formData.email) await updateUserEmail(uid, password, formData.email);
      setSuccess("Profile saved successfully!");
      setTimeout(() => navigate("/view-profile"), 1200);
    } catch (err) {
      const msg = err.message || "";
      // Robust check for Odoo duplicate phone/email errors
      if (msg.includes("already exists")) {
        if (msg.toLowerCase().includes("phone")) {
          setError("Mobile number already exists");
        } else if (msg.toLowerCase().includes("email")) {
          setError("Email address already exists");
        } else {
          setError(msg);
        }
      } else {
        setError(msg);
      }
    } finally {
      setSaving(false);
    }
  };




  const showBank = formData.payment_type === "bank";
  const showUpi = formData.payment_type === "upi";
  const showGateway = formData.payment_type === "gateway";

  return (
    <div style={S.page} className="dp-edit-page">
      {loading && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 10000,
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
          background: "rgba(248, 250, 252, 0.9)", backdropFilter: "blur(4px)"
        }}>
          <div style={{ width: 50, height: 50, borderRadius: "50%", borderWidth: "4px", borderStyle: "solid", borderColor: "#e2e8f0", borderTopColor: "#7c3aed", animation: "pl-spin 0.75s linear infinite", marginBottom: 20 }} />
          <p style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "#6366f1" }}>Loading your profile…</p>
          <style>{`@keyframes pl-spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet" />
      <style>{GLOBAL_CSS}</style>

      {/* Header */}
      <div style={S.header}>
        <button style={S.backBtn} onClick={() => navigate("/view-profile")}>
          ← Back to Profile
        </button>
        <div>
          <h1 style={S.headerTitle}>Edit Profile</h1>
          <p style={S.headerSub}>Update your personal, address & payment details</p>
        </div>
      </div>

      {/* Card */}
      <div style={S.card} className="dp-edit-card">

        {/* Tab bar */}
        <div style={S.tabBar}>
          {TABS.map(tab => (
            <button
              key={tab.id}
              style={{ ...S.tab, ...(activeTab === tab.id ? S.tabActive : {}) }}
              onClick={() => setActiveTab(tab.id)}
            >
              <span>{tab.icon}</span> {tab.label}
            </button>
          ))}
        </div>

        {/* Messages */}
        <div style={{ padding: "0 28px" }}>
          {error && (
            <div style={S.errorBox}>
              {error}
              {isFatalAuthError(error) && <div style={{ marginTop: 5, color: "#94a3b8", fontSize: 12 }}>Logging you out…</div>}
            </div>
          )}
          {success && <div style={S.successBox}>{success}</div>}
        </div>

        {/* Tab content */}
        <div style={S.tabContent} className="dp-edit-tab-content">

          {/* ── PERSONAL ── */}
          {activeTab === "personal" && (
            <div style={S.formGrid}>
              <FormRow>
                <FormField label="Full Name">
                  <Input name="name" value={formData.name} onChange={handleChange} placeholder="Your full name" />
                </FormField>
                <FormField label="Email Address">
                  <Input name="email" type="email" value={formData.email} onChange={handleChange} placeholder="you@example.com" required />
                </FormField>
              </FormRow>
              <FormRow>
                <FormField label="Phone Number">
                  <Input
                    name="phone" value={formData.phone} placeholder="9876543210" maxLength={10} required
                    onChange={handlePhoneChange}
                  />
                </FormField>

                <FormField label="LTX">
                  <Input
                    name="ltx"
                    value={formData.ltx}
                    placeholder="LTX 0000000000"
                    readOnly={true}
                    onChange={handleChange}
                    style={{ background: "#f8fafc", cursor: "not-allowed", fontWeight: "700", color: "#6366f1" }}
                  />
                </FormField>
                {!isAdmin && (
                  <FormField label="Referral Code">
                    <Input
                      name="sponsor_code"
                      value={formData.sponsor_code}
                      readOnly={true}
                      onChange={handleChange}
                      placeholder="Enter referral code"
                      disabled={!!getCookie("mlm_referral_code") && !formData.parent_id}
                      style={getCookie("mlm_referral_code") && !formData.parent_id ? { background: "#f1f5f9", cursor: "not-allowed" } : {}}
                    />
                  </FormField>
                )}
              </FormRow>
            </div>
          )}

          {/* ── ADDRESS ── */}
          {activeTab === "address" && (
            <div style={S.formGrid}>
              <FormField label="Street Address *">
                <Input name="street" value={formData.street} onChange={handleChange} placeholder="123 Main Street" />
              </FormField>
              <FormField label="Apartment / Suite">
                <Input name="street2" value={formData.street2} onChange={handleChange} placeholder="Apt, Suite, Floor…" />
              </FormField>
              <FormRow>
                <FormField label="City *">
                  <Input name="city" value={formData.city} onChange={handleChange} placeholder="City" />
                </FormField>
                <FormField label="Postal Code *">
                  <Input
                    name="zip" value={formData.zip} placeholder="ZIP / Postcode" maxLength={6}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, "").slice(0, 6);
                      handleChange({ target: { name: "zip", value } });
                    }}
                  />
                </FormField>
              </FormRow>
              <FormRow>
                <FormField label="Country *">
                  <div ref={countryRef} style={{ position: "relative" }}>
                    <SelectBtn
                      onClick={() => { setShowCountryDd(v => !v); setCountrySearch(""); }}
                      value={selectedCountry?.name} placeholder="Select country"
                    />
                    {showCountryDd && (
                      <Dropdown
                        search={countrySearch} onSearch={setCountrySearch}
                        searchPlaceholder="Search country…"
                        items={filteredCountries}
                        onSelect={(c) => handleCountryChange(c.id)}
                      />
                    )}
                  </div>
                </FormField>
                <FormField label="State *">
                  <div ref={stateRef} style={{ position: "relative" }}>
                    <SelectBtn
                      onClick={() => { if (states.length) { setShowStateDd(v => !v); setStateSearch(""); } }}
                      value={selectedState?.name}
                      placeholder={states.length === 0 ? "Select a country first" : "Select state"}
                      disabled={states.length === 0}
                    />
                    {showStateDd && states.length > 0 && (
                      <Dropdown
                        search={stateSearch} onSearch={setStateSearch}
                        searchPlaceholder="Search state…"
                        items={filteredStates}
                        onSelect={(s) => {
                          setFormData(prev => ({ ...prev, state_id: s.id }));
                          setShowStateDd(false); setStateSearch("");
                        }}
                      />
                    )}
                  </div>
                </FormField>
              </FormRow>
            </div>
          )}

          {/* ── PAYMENT ── */}
          {activeTab === "payment" && (
            <div style={S.formGrid}>
              {/* Payment type selector */}
              <FormField label="">
                <div style={S.payTypeGrid} className="dp-edit-pay-type-grid">
                  {PAYMENT_TYPES.map(pt => (
                    <button
                      key={pt.value}
                      type="button"
                      style={{
                        ...S.payTypeBtn,
                        ...(formData.payment_type === pt.value
                          ? S.payTypeBtnActive
                          : {}),
                      }}
                      onClick={() =>
                        setFormData(prev => ({
                          ...prev,
                          payment_type: pt.value,
                        }))
                      }
                    >
                      <span style={{ fontSize: 20 }}>{pt.icon}</span>
                      <span style={{ fontSize: 13, fontWeight: 600 }}>{pt.label}</span>
                    </button>
                  ))}
                </div>
              </FormField>

              {/* Bank fields */}
              <FormRow>
                <FormField label="Bank Name">
                  <Input
                    name="bank_name"
                    value={formData.bank_name}
                    onChange={handleChange}
                    placeholder="Enter bank name"
                    required
                  />
                </FormField>

                <FormField label="Bank Branch">
                  <Input
                    name="bank_branch"
                    value={formData.bank_branch}
                    onChange={handleChange}
                    placeholder="Branch name or city"
                    required
                  />
                </FormField>
              </FormRow>

              <FormRow>
                <FormField label="Account Holder Name">
                  <Input
                    name="bank_account_name"
                    value={formData.bank_account_name}
                    onChange={handleChange}
                    placeholder="As per bank records"
                    required
                  />
                </FormField>

                <FormField label="Account Number">
                  <Input
                    name="bank_account_number"
                    value={formData.bank_account_number}
                    onChange={handleChange}
                    placeholder="Enter account number"
                    required
                  />
                </FormField>
              </FormRow>

              <FormRow>
                <FormField label="Account Type">
                  <div style={S.segControl}>
                    {ACCOUNT_TYPES.map((at) => (
                      <button
                        key={at.value}
                        type="button"
                        style={{
                          ...S.segBtn,
                          ...(formData.bank_account_type === at.value
                            ? S.segBtnActive
                            : {}),
                        }}
                        onClick={() =>
                          setFormData((prev) => ({
                            ...prev,
                            bank_account_type: at.value,
                          }))
                        }
                      >
                        {at.label}
                      </button>
                    ))}
                  </div>
                </FormField>

                <FormField label="IFSC Code">
                  <Input
                    name="bank_ifsc_code"
                    value={formData.bank_ifsc_code}
                    onChange={handleChange}
                    placeholder="e.g. SBIN0001234"
                    style={{
                      fontFamily: "monospace",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                    }}
                    required
                  />
                </FormField>
              </FormRow>

              {/* UPI fields
              {showUpi && (
                <FormField label="UPI Reference Code / UPI ID">
                  <Input name="upi_reference_code" value={formData.upi_reference_code} onChange={handleChange} placeholder="yourname@upi or phone number" />
                </FormField>
              )}

              {/* Gateway fields */}
              {/* {showGateway && (
                <FormField label="Payment Gateway Reference">
                  <Input name="payment_gateway_reference" value={formData.payment_gateway_reference} onChange={handleChange} placeholder="Gateway account ID or reference" />
                </FormField>
              )}  */}

              {/* Cash — no extra fields
              {formData.payment_type === "cash" && (
                <div style={S.cashNote}>
                  <span style={{ fontSize: 28 }}>💵</span>
                  <div>
                    <div style={{ fontWeight: 600, color: "#1e293b", fontSize: 14 }}>Cash Payment Selected</div>
                    <div style={{ color: "#64748b", fontSize: 13, marginTop: 4 }}>No additional banking details required.</div>
                  </div>
                </div>
              )} */}

              {/* {!formData.payment_type && (
                <div style={S.cashNote}>
                  <span style={{ fontSize: 28 }}>👆</span>
                  <div style={{ color: "#64748b", fontSize: 13 }}>Select a payment type above to continue.</div>
                </div>
              )} */}
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div style={S.footer} className="dp-edit-footer">
          <div style={S.footerNav}>
            {activeTab !== "personal" && (
              <button style={S.prevBtn} onClick={() => {
                const idx = TABS.findIndex(t => t.id === activeTab);
                if (idx > 0) setActiveTab(TABS[idx - 1].id);
              }}>← Previous</button>
            )}
            {activeTab !== "payment" ? (
              <button style={S.nextBtn} onClick={async () => {
                if (activeTab === "personal") {
                  if (!formData.email?.trim() || !formData.phone?.trim()) {
                    setError("Email and Phone are required.");
                    return;
                  }
                  // Phone & Email duplication check
                  setSaving(true);
                  try {
                    const { rpc, DB } = await import("../script/rpc");
                    const [dupPhone, dupEmail] = await Promise.all([
                      rpc("object", "execute_kw", [
                        DB, uid, password,
                        "res.partner", "search",
                        [[["phone", "=", formData.phone], ["id", "!=", partnerId]]]
                      ]),
                      rpc("object", "execute_kw", [
                        DB, uid, password,
                        "res.partner", "search",
                        [[["email", "=", formData.email], ["id", "!=", partnerId]]]
                      ])
                    ]);

                    if (dupPhone && dupPhone.length > 0) {
                      setSaving(false);
                      setError("Mobile number already exists");
                      return;
                    }
                    if (dupEmail && dupEmail.length > 0) {
                      setSaving(false);
                      setError("Email address already exists");
                      return;
                    }
                  } catch (e) {
                    console.error("Duplication check failed:", e);
                  } finally {
                    setSaving(false);
                  }
                }

                if (
                  activeTab === "address" &&
                  (!formData.street?.trim() || !formData.city?.trim() || !formData.zip?.trim() || !formData.country_id || !formData.state_id || !formData.street2?.trim())
                ) {
                  setError("All The Fields are Required..");
                  return;
                }
                setError("");

                const idx = TABS.findIndex(t => t.id === activeTab);
                if (idx < TABS.length - 1) {
                  setActiveTab(TABS[idx + 1].id);
                }
              }}>Next →</button>
            ) : (
              <button
                style={{
                  ...S.saveBtn,
                  ...(saving || (!isAdmin && !formData.sponsor_code) ? S.saveBtnDisabled : {}),
                }}
                disabled={saving || (!isAdmin && !formData.sponsor_code)}
                onClick={handleSubmit}
                onMouseEnter={e => { if (!saving) e.currentTarget.style.background = "#4f46e5"; }}
                onMouseLeave={e => { if (!saving) e.currentTarget.style.background = "#6366f1"; }}
              >
                {saving
                  ? <><Spinner /> Saving…</>
                  : "✓ Save Profile"
                }
              </button>
            )}
          </div>
          {/* Tab dots indicator */}
          <div style={S.dots}>
            {TABS.map(t => (
              <div key={t.id} style={{ ...S.dot, ...(activeTab === t.id ? S.dotActive : {}) }} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Shared small components ── */
function FormRow({ children }) {
  return <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>{React.Children.map(children, c => <div style={{ flex: "1 1 200px", minWidth: 0 }}>{c}</div>)}</div>;
}

function FormField({ label, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <label style={{ fontSize: 13, fontWeight: 600, color: "#374151", letterSpacing: "-0.1px" }}>{label}</label>
      {children}
    </div>
  );
}

const Input = React.forwardRef(({ style, ...props }, ref) => (
  <input
    ref={ref} {...props}
    style={{
      width: "100%", boxSizing: "border-box",
      padding: "10px 13px", borderRadius: 8,
      border: "1.5px solid #e5e7eb",
      fontSize: 14, fontFamily: "inherit", color: "#111827",
      background: "#fafafa", outline: "none",
      transition: "border-color 0.15s, box-shadow 0.15s",
      ...style,
    }}
    onFocus={e => { e.target.style.borderColor = "#6366f1"; e.target.style.boxShadow = "0 0 0 3px rgba(99,102,241,0.12)"; }}
    onBlur={e => { e.target.style.borderColor = "#e5e7eb"; e.target.style.boxShadow = "none"; }}
  />
));

function SelectBtn({ onClick, value, placeholder, disabled }) {
  return (
    <div
      onClick={!disabled ? onClick : undefined}
      style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "10px 13px", borderRadius: 8, border: "1.5px solid #e5e7eb",
        fontSize: 14, fontFamily: "inherit",
        background: disabled ? "#f3f4f6" : "#fafafa",
        color: value ? "#111827" : "#9ca3af",
        cursor: disabled ? "not-allowed" : "pointer",
        userSelect: "none", opacity: disabled ? 0.7 : 1,
      }}
    >
      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{value || placeholder}</span>
      {!disabled && <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ flexShrink: 0, marginLeft: 8 }}><path d="M2 4l4 4 4-4" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>}
    </div>
  );
}

function Dropdown({ search, onSearch, searchPlaceholder, items, onSelect }) {
  return (
    <div style={{
      position: "absolute", top: "calc(100% + 6px)", left: 0, right: 0,
      background: "#fff", border: "1.5px solid #e5e7eb", borderRadius: 10,
      boxShadow: "0 8px 24px rgba(0,0,0,0.10)", zIndex: 999, overflow: "hidden",
    }}>
      <input
        autoFocus
        style={{
          width: "100%", boxSizing: "border-box",
          padding: "9px 12px", border: "none", borderBottom: "1px solid #f0f0f0",
          outline: "none", fontSize: 13, fontFamily: "inherit",
          background: "#fafafa", color: "#111",
        }}
        placeholder={searchPlaceholder}
        value={search}
        onChange={e => onSearch(e.target.value)}
        onClick={e => e.stopPropagation()}
      />
      <div style={{ maxHeight: 180, overflowY: "auto" }}>
        {items.length === 0
          ? <div style={{ padding: "14px", textAlign: "center", color: "#9ca3af", fontSize: 13 }}>No results found</div>
          : items.map(item => (
            <div key={item.id}
              style={{ padding: "9px 14px", cursor: "pointer", fontSize: 13, color: "#1e1e2e", borderBottom: "1px solid #f5f5f5", transition: "background 0.1s" }}
              onMouseEnter={e => e.currentTarget.style.background = "#f8f8ff"}
              onMouseLeave={e => e.currentTarget.style.background = ""}
              onClick={() => onSelect(item)}
            >
              {item.name}
            </div>
          ))}
      </div>
    </div>
  );
}

function Spinner() {
  return <div style={{ width: 16, height: 16, borderRadius: "50%", border: "2px solid rgba(255,255,255,0.35)", borderTopColor: "#fff", animation: "ep-spin 0.7s linear infinite", display: "inline-block" }} />;
}

function Loader() {
  return (
    <div style={{ height: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#f5f6fa" }}>
      <div style={{ width: 36, height: 36, borderRadius: "50%", border: "3px solid #e5e7eb", borderTopColor: "#6366f1", animation: "ep-spin 0.8s linear infinite" }} />
      <style>{`@keyframes ep-spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

/* ── Styles ── */
const S = {
  page: {
    minHeight: "100vh", background: "#f1f5f9",
    fontFamily: "'DM Sans', 'Inter', system-ui, sans-serif",
    padding: "28px 16px 40px",
  },
  header: {
    maxWidth: 720, margin: "0 auto 20px",
    display: "flex", alignItems: "flex-start", gap: 16,
  },
  backBtn: {
    background: "none", border: "1px solid #e2e8f0", borderRadius: 8,
    padding: "8px 14px", fontSize: 13, color: "#64748b", cursor: "pointer",
    fontFamily: "inherit", fontWeight: 500, whiteSpace: "nowrap", marginTop: 4,
    transition: "all 0.15s",
  },
  headerTitle: { margin: 0, fontSize: 22, fontWeight: 700, color: "#0f172a", letterSpacing: "-0.4px" },
  headerSub: { margin: "4px 0 0", fontSize: 13, color: "#64748b" },
  card: {
    maxWidth: 720, margin: "0 auto",
    background: "#fff", borderRadius: 18,
    boxShadow: "0 2px 24px rgba(0,0,0,0.07), 0 0 0 1px rgba(0,0,0,0.04)",
    overflow: "visible",
  },
  tabBar: {
    display: "flex", borderBottom: "1px solid #f0f0f0",
    padding: "0 8px",
  },
  tab: {
    flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
    padding: "16px 12px", border: "none", background: "none",
    fontSize: 13, fontWeight: 500, color: "#94a3b8",
    cursor: "pointer", fontFamily: "inherit",
    borderBottomWidth: "2px", borderBottomStyle: "solid", borderBottomColor: "transparent",
    transition: "all 0.15s",
  },
  tabActive: {
    color: "#6366f1", borderBottomColor: "#6366f1", fontWeight: 700,
  },
  tabContent: { padding: "24px 28px 8px" },
  formGrid: { display: "flex", flexDirection: "column", gap: 16 },
  errorBox: {
    background: "#fef2f2", border: "1px solid #fecaca",
    borderRadius: 8, padding: "10px 14px",
    color: "#dc2626", fontSize: 13, marginBottom: 12, textAlign: "center",
  },
  successBox: {
    background: "#f0fdf4", border: "1px solid #bbf7d0",
    borderRadius: 8, padding: "10px 14px",
    color: "#16a34a", fontSize: 13, fontWeight: 600, marginBottom: 12, textAlign: "center",
  },
  footer: {
    padding: "20px 28px 24px", borderTop: "1px solid #f1f5f9",
    display: "flex", flexDirection: "column", gap: 16,
  },
  footerNav: { display: "flex", justifyContent: "flex-end", gap: 10 },
  prevBtn: {
    padding: "10px 20px", borderRadius: 9,
    border: "1.5px solid #e5e7eb", background: "#fff",
    color: "#374151", fontSize: 13.5, fontWeight: 600,
    cursor: "pointer", fontFamily: "inherit", transition: "all 0.15s",
  },
  nextBtn: {
    padding: "10px 22px", borderRadius: 9,
    border: "none", background: "#6366f1",
    color: "#fff", fontSize: 13.5, fontWeight: 600,
    cursor: "pointer", fontFamily: "inherit", transition: "background 0.15s",
  },
  saveBtn: {
    padding: "11px 26px", borderRadius: 9, border: "none", background: "#6366f1",
    color: "#fff", fontSize: 14, fontWeight: 700,
    cursor: "pointer", fontFamily: "inherit",
    display: "flex", alignItems: "center", gap: 8,
    transition: "background 0.15s",
    boxShadow: "0 4px 12px rgba(99,102,241,0.35)",
  },
  saveBtnDisabled: {
    background: "#c4b5fd", cursor: "not-allowed", boxShadow: "none",
  },
  dots: { display: "flex", justifyContent: "center", gap: 6 },
  dot: {
    width: 6, height: 6, borderRadius: "50%", background: "#e2e8f0", transition: "all 0.2s",
  },
  dotActive: { background: "#6366f1", width: 18, borderRadius: 3 },
  // Payment type grid
  payTypeGrid: {
    display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10,
  },
  payTypeBtn: {
    display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
    gap: 6, padding: "14px 10px", borderRadius: 10,
    border: "1.5px solid #e5e7eb", background: "#fafafa",
    cursor: "pointer", fontFamily: "inherit",
    transition: "all 0.15s",
  },
  payTypeBtnActive: {
    border: "1.5px solid #6366f1", background: "#eef2ff",
    boxShadow: "0 0 0 3px rgba(99,102,241,0.12)",
  },
  segControl: {
    display: "flex", borderRadius: 8, overflow: "hidden",
    border: "1.5px solid #e5e7eb",
  },
  segBtn: {
    flex: 1, padding: "10px 8px",
    border: "none", borderRight: "1px solid #e5e7eb",
    background: "#fafafa", color: "#6b7280",
    fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: "inherit",
    transition: "all 0.15s",
  },
  segBtnActive: { background: "#6366f1", color: "#fff", fontWeight: 700 },
  cashNote: {
    display: "flex", alignItems: "center", gap: 14,
    padding: "18px 20px", background: "#f8fafc",
    border: "1.5px solid #e2e8f0", borderRadius: 10,
  },
};

const GLOBAL_CSS = `
  @keyframes ep-spin { to { transform: rotate(360deg); } }
  * { box-sizing: border-box; }

  @media (max-width: 768px) {
    .dp-edit-page { padding: 12px 10px 60px !important; }
    .dp-edit-card { border-radius: 12px !important; }
    .dp-edit-tab-content { padding: 16px 14px 8px !important; }
    .dp-edit-footer { padding: 16px 14px 20px !important; }
    .dp-edit-pay-type-grid { grid-template-columns: repeat(2, 1fr) !important; }
  }
`;